import React from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import PlanningRoomBoundary from './views/PlanningRoomBoundary';
import { useForm } from 'react-hook-form';
import {
  Flex,
  Box,
  Container,
  Heading,
  Text,
  Input,
  Button,
  FormControl,
  FormErrorMessage,
  IconButton,
  Icon,
  useColorMode,
  useColorModeValue,
  Image,
} from '@chakra-ui/core';
import { HiSun, HiMoon } from 'react-icons/hi';
import { track } from './analytics';

function App() {
  const history = useHistory();
  const { handleSubmit, register, errors } = useForm();
  const onSubmit = ({ roomId }: { [x: string]: string }) => {
    track('room_created_clicked');
    history.push(`/${roomId}`);
  };
  const { colorMode, toggleColorMode } = useColorMode();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  return (
    <div className="App">
      <Box bg="linear-gradient(to right, #6b21a8, #2563eb)" shadow="md">
        <Container>
          <Flex w="100%" align="center" py={3}>
            <Image
              src={`${process.env.PUBLIC_URL}/logo192.png`}
              alt=""
              boxSize="32px"
              borderRadius="md"
              mr={2}
            />
            <Heading fontSize="xl" color="white" letterSpacing="tight">
              Planning Poker
            </Heading>
            <IconButton
              onClick={toggleColorMode}
              ml="auto"
              aria-label="Toggle color mode"
              variant="ghost"
              color="white"
              _hover={{ bg: 'whiteAlpha.200' }}
              icon={
                colorMode === 'light' ? (
                  <Icon as={HiMoon} />
                ) : (
                  <Icon as={HiSun} />
                )
              }
            />
          </Flex>
        </Container>
      </Box>
      <Switch>
        <Route<{ roomId: string }>
          path="/:roomId"
          render={({ match }) => (
            <PlanningRoomBoundary
              key={match.params.roomId}
              roomId={match.params.roomId}
            />
          )}
        />
        <Route path="/">
          <Container mt={12} maxW="md">
            <Flex direction="column" align="center" mb={8}>
              <Image
                boxSize={['200px', '240px', '280px']}
                objectFit="cover"
                src={`${process.env.PUBLIC_URL}/logo512.png`}
                borderRadius="2xl"
                alt="Planning Poker"
              />
            </Flex>
            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={cardBorder}
              borderRadius="xl"
              p={8}
              shadow="lg">
              <Heading fontSize="lg" mb={1}>
                Start or join a room
              </Heading>
              <Text fontSize="sm" color="gray.500" mb={5}>
                Enter a room name to begin your planning session.
              </Text>
              <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <FormControl isInvalid={!!errors.roomId}>
                  <Input
                    name="roomId"
                    data-lpignore="true"
                    placeholder="e.g. sprint-42"
                    size="lg"
                    borderRadius="lg"
                    focusBorderColor="purple.400"
                    ref={register({
                      required: 'Required',
                      pattern: {
                        value: /^[a-zA-Z0-9_-]{1,64}$/,
                        message:
                          'Letters, numbers, hyphens, and underscores only (max 64 chars)',
                      },
                    })}
                    isInvalid={!!errors.roomId}
                  />
                  <FormErrorMessage>{errors.roomId?.message}</FormErrorMessage>
                </FormControl>
                <Button
                  mt={4}
                  w="full"
                  size="lg"
                  colorScheme="purple"
                  borderRadius="lg"
                  type="submit">
                  Enter Room
                </Button>
              </form>
            </Box>
          </Container>
        </Route>
      </Switch>
    </div>
  );
}

export default App;
